import {
  Anchor,
  AspectRatio,
  Badge,
  Button,
  Card,
  Grid,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconCheck,
  IconExternalLink,
  IconLink,
  IconReplace,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  Collab,
  CollabContent,
  UserType,
} from "../../../../../../gql/__generated__/graphql";
import GenericConfirmationModal from "../../../../../components/Modals/GenericConfirmationModal";
import TikTokEmbed from "../../../../../components/TikTokEmbed/TikTokEmbed";

interface ReviewContentModalProps {
  opened: boolean;
  onClose: () => void;
  collab: Collab;
  userRole: UserType;
  onAccept: (contentId: string) => void;
  onRequestChanges: (message?: string) => void;
  onRequestContentChanges: (contentId: string, feedbackMessage: string) => void;
  onReplaceContent?: (contentId: string, newContentUrl: string) => void;
}

function ReviewContentModal({
  opened,
  onClose,
  collab,
  userRole,
  onAccept,
  onRequestChanges,
  onRequestContentChanges,
  onReplaceContent,
}: ReviewContentModalProps) {
  const submittedContent = collab.submittedContent;

  // Initialize locallyApprovedIds with already approved content IDs
  const [locallyApprovedIds, setLocallyApprovedIds] = useState<Set<string>>(
    () => {
      const approvedIds =
        submittedContent
          ?.filter((c) => c.status === "approved")
          .map((c) => c.id) ?? [];
      return new Set(approvedIds);
    }
  );

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedContentId, setSelectedContentId] = useState<string | null>(
    null
  );
  const [videoErrors, setVideoErrors] = useState<Record<string, boolean>>({});
  const [showRequestChangesInput, setShowRequestChangesInput] = useState<
    Record<string, boolean>
  >({});
  const [feedbackMessages, setFeedbackMessages] = useState<
    Record<string, string>
  >({});
  const [replacementUrls, setReplacementUrls] = useState<
    Record<string, string>
  >({});
  const [showReplaceInput, setShowReplaceInput] = useState<
    Record<string, boolean>
  >({});

  const approvedContentCount = locallyApprovedIds.size;
  const totalContentCount = collab.submittedContent?.length ?? 0;

  const handleAcceptClick = (contentId: string) => {
    // Check if this is the last content to approve
    if (approvedContentCount + 1 === totalContentCount) {
      setSelectedContentId(contentId);
      setShowConfirmation(true);
    } else {
      // Mark as locally approved for instant UI feedback
      setLocallyApprovedIds((prev) => new Set(prev).add(contentId));
      onAccept(contentId);
    }
  };

  const handleConfirmAccept = () => {
    if (selectedContentId) {
      // Mark as locally approved for instant UI feedback
      setLocallyApprovedIds((prev) => new Set(prev).add(selectedContentId));
      onAccept(selectedContentId);
    }
    setShowConfirmation(false);
    onClose();
  };

  const handleVideoError = (contentId: string) => {
    setVideoErrors((prev) => ({ ...prev, [contentId]: true }));
  };

  const renderContent = (content: CollabContent) => {
    const isVideo = content.contentUrl.includes("video");
    const isTikTok = content.contentUrl.includes("tiktok.com");
    const isApproved = content.status === "approved";
    const isChangesRequested = content.status === "changes_requested";
    const isLocallyApproved = locallyApprovedIds.has(content.id);
    const showAsApproved = isApproved || isLocallyApproved;
    const hasVideoError = videoErrors[content.id];
    const showingFeedbackInput = showRequestChangesInput[content.id];

    return (
      <Grid.Col span={{ base: 12, md: 6 }} key={content.id}>
        <Card withBorder p="md">
          <Stack>
            {/* Content Link */}
            <Group gap="xs">
              <IconExternalLink size={16} />
              <Anchor
                href={content.contentUrl}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                fw={500}
              >
                View Original Content
              </Anchor>
            </Group>

            {/* Media Display or Error Fallback */}
            {hasVideoError ? (
              <Card withBorder bg="gray.0" p="xl">
                <Stack align="center" gap="md">
                  <IconAlertCircle
                    size={48}
                    color="var(--mantine-color-red-6)"
                  />
                  <Text size="lg" fw={500} ta="center">
                    Video Failed to Load
                  </Text>
                  <Text size="sm" c="dimmed" ta="center">
                    The video could not be displayed. Please use the link above
                    to view the content directly.
                  </Text>
                  <Button
                    component="a"
                    href={content.contentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="light"
                    leftSection={<IconExternalLink size={16} />}
                  >
                    Open Content Link
                  </Button>
                </Stack>
              </Card>
            ) : isTikTok ? (
              <TikTokEmbed videoUrl={content.contentUrl} />
            ) : isVideo ? (
              <AspectRatio ratio={9 / 16}>
                <video
                  src={content.contentUrl}
                  controls
                  onError={() => handleVideoError(content.id)}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </AspectRatio>
            ) : (
              <AspectRatio ratio={16 / 9}>
                <img
                  src={content.contentUrl}
                  alt="Submitted content"
                  onError={() => handleVideoError(content.id)}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </AspectRatio>
            )}

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Submitted on:{" "}
                {new Date(content.submittedAt).toLocaleDateString()}
              </Text>
              {showAsApproved && (
                <motion.div
                  initial={
                    isLocallyApproved ? { scale: 0.5, opacity: 0 } : false
                  }
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 20,
                  }}
                >
                  <Badge color="green" leftSection={<IconCheck size={12} />}>
                    Approved
                  </Badge>
                </motion.div>
              )}
              {isChangesRequested && (
                <Badge color="yellow" variant="light">
                  Changes Requested
                </Badge>
              )}
            </Group>

            {/* Display existing feedback if changes were requested */}
            {isChangesRequested && content.feedbackMessage && (
              <Card withBorder bg="yellow.0" p="sm">
                <Stack gap="xs">
                  <Text size="sm" fw={600} c="yellow.9">
                    Feedback from Brand:
                  </Text>
                  <Text size="sm" c="gray.8">
                    {content.feedbackMessage}
                  </Text>
                  {content.feedbackAt && (
                    <Text size="xs" c="dimmed">
                      Requested on:{" "}
                      {new Date(content.feedbackAt).toLocaleDateString()}
                    </Text>
                  )}
                </Stack>
              </Card>
            )}
            {userRole === "BRAND_USER" && !showAsApproved && (
              <Stack gap="sm">
                <Group justify="flex-end">
                  <Button
                    variant="outline"
                    color="primary"
                    onClick={() =>
                      setShowRequestChangesInput((prev) => ({
                        ...prev,
                        [content.id]: !prev[content.id],
                      }))
                    }
                  >
                    Request Changes
                  </Button>
                  <motion.div
                    whileTap={{ scale: 0.95 }}
                    style={{ display: "inline-block" }}
                  >
                    <Button
                      color="secondary"
                      onClick={() => handleAcceptClick(content.id)}
                    >
                      Accept Content
                    </Button>
                  </motion.div>
                </Group>
                {showingFeedbackInput && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.2 }}
                  >
                    <Stack gap="sm">
                      <Textarea
                        placeholder="Describe what changes you'd like the creator to make..."
                        value={feedbackMessages[content.id] || ""}
                        onChange={(e) =>
                          setFeedbackMessages((prev) => ({
                            ...prev,
                            [content.id]: e.target.value,
                          }))
                        }
                        minRows={3}
                        autosize
                      />
                      <Group justify="flex-end">
                        <Button
                          variant="filled"
                          color="primary"
                          onClick={() => {
                            const feedback = feedbackMessages[content.id];
                            if (feedback?.trim()) {
                              onRequestContentChanges(content.id, feedback);
                              setShowRequestChangesInput((prev) => ({
                                ...prev,
                                [content.id]: false,
                              }));
                              setFeedbackMessages((prev) => ({
                                ...prev,
                                [content.id]: "",
                              }));
                            }
                          }}
                          disabled={!feedbackMessages[content.id]?.trim()}
                        >
                          Request Changes for This Content
                        </Button>
                      </Group>
                    </Stack>
                  </motion.div>
                )}
              </Stack>
            )}
            {userRole === "BRAND_USER" && showAsApproved && !isApproved && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Group justify="center">
                  <Badge
                    size="lg"
                    color="green"
                    variant="light"
                    leftSection={<IconCheck size={16} />}
                  >
                    Content Approved
                  </Badge>
                </Group>
              </motion.div>
            )}

            {/* Creator replace content UI */}
            {userRole === "CREATOR" && !isApproved && onReplaceContent && (
              <Stack gap="sm">
                <Text size="sm" c="dimmed">
                  Check your messages for any feedback from the brand.
                </Text>
                <Button
                  variant="light"
                  color="primary"
                  leftSection={<IconReplace size={16} />}
                  onClick={() =>
                    setShowReplaceInput((prev) => ({
                      ...prev,
                      [content.id]: !prev[content.id],
                    }))
                  }
                >
                  Replace This Content
                </Button>
                {showReplaceInput[content.id] && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.2 }}
                  >
                    <Stack gap="sm">
                      <TextInput
                        placeholder="Paste new content URL (TikTok, YouTube, image, etc.)"
                        value={replacementUrls[content.id] || ""}
                        onChange={(e) =>
                          setReplacementUrls((prev) => ({
                            ...prev,
                            [content.id]: e.target.value,
                          }))
                        }
                        leftSection={<IconLink size={16} />}
                      />
                      <Group justify="flex-end">
                        <Button
                          variant="filled"
                          color="secondary"
                          onClick={() => {
                            const url = replacementUrls[content.id];
                            if (url?.trim()) {
                              onReplaceContent(content.id, url.trim());
                              setShowReplaceInput((prev) => ({
                                ...prev,
                                [content.id]: false,
                              }));
                              setReplacementUrls((prev) => ({
                                ...prev,
                                [content.id]: "",
                              }));
                            }
                          }}
                          disabled={!replacementUrls[content.id]?.trim()}
                        >
                          Replace Content
                        </Button>
                      </Group>
                    </Stack>
                  </motion.div>
                )}
              </Stack>
            )}
          </Stack>
        </Card>
      </Grid.Col>
    );
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={<Title order={3}>Review Submitted Content</Title>}
        size="xl"
        centered
      >
        <Stack>
          {submittedContent && submittedContent.length > 0 ? (
            <Grid>{submittedContent.map(renderContent)}</Grid>
          ) : (
            <Text>No content has been submitted for this collab yet.</Text>
          )}
        </Stack>
      </Modal>
      <GenericConfirmationModal
        opened={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        title="Final Acceptance"
        text="This is the last content to accept. Accepting this will close out the Collab and pay the Creator."
        onConfirm={handleConfirmAccept}
        confirmText="Accept and Complete"
      />
    </>
  );
}

export default ReviewContentModal;
